import { createClient } from '@supabase/supabase-js';
import { createCrossSubdomainAuthStorage } from '@rehub/supabase/auth-storage';
import { getAdminRole as getAdminRoleShared, checkAdminStatus as checkAdminStatusShared } from '@rehub/admin';
import { StudioBuildStatus, FloorPlanOrderStatus, FloorPlanPropertyType } from '../types';
import { parseWorldsAccess, type WorldsAccess } from '../utils/worldsAccess';
import type { WorldsSchoolTier } from '@rehub/pricing';
export type { WorldsAccess } from '../utils/worldsAccess';

// Explicitly access environment variables to ensure Vite's static replacement works correctly.
const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';

export const isConfigured = !!(supabaseUrl && supabaseAnonKey && supabaseUrl.startsWith('http') && !supabaseUrl.includes('placeholder'));
export { supabaseUrl };

if (!isConfigured) {
    console.warn(
        'Supabase environment variables missing or invalid. Authentication will be disabled.\n' +
        'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment (e.g. .env.local or Vercel Settings).'
    );
}

const validUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co';
const validKey = isConfigured ? supabaseAnonKey : 'placeholder';

export const supabase = createClient(validUrl, validKey, {
    auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        storage: createCrossSubdomainAuthStorage(),
    },
});

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
    openBillingPortalShared(supabase, returnUrl);

// --- CLIENT IP TRACKING ---

let cachedClientIP: string | null = null;
let ipFetchPromise: Promise<string | null> | null = null;

export const fetchClientIP = async (): Promise<string | null> => {
    if (!isConfigured) return null;
    if (cachedClientIP) return cachedClientIP;
    if (ipFetchPromise) return ipFetchPromise;
    ipFetchPromise = (async () => {
        try {
            const response = await fetch(`${supabaseUrl}/functions/v1/get-client-ip`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            if (response.ok) {
                const data = await response.json();
                cachedClientIP = data.ip || null;
                return cachedClientIP;
            }
        } catch (err) {
            console.warn('Failed to fetch client IP:', err);
        }
        return null;
    })();
    return ipFetchPromise;
};

export const getClientIP = (): string | null => cachedClientIP;

export const logEventWithIP = async (
    eventType: string,
    userId?: string | null,
    userEmail?: string | null,
    metadata?: Record<string, any>
): Promise<void> => {
    if (!isConfigured) return;
    try {
        await fetch(`${supabaseUrl}/functions/v1/get-client-ip`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                eventType,
                userId: userId || null,
                userEmail: userEmail || null,
                metadata: { ...metadata, source_app: 'tours' },
            }),
        });
    } catch (err) {
        console.warn('Failed to log event with IP:', err);
    }
};

// --- HELPER FUNCTIONS ---

export const storePendingMarketingConsent = async (email: string): Promise<void> => {
    if (!isConfigured) return;
    try {
        await supabase
            .from('pending_marketing_consent')
            .upsert({ email }, { onConflict: 'email' });
    } catch (err) {
        console.error('Failed to store pending marketing consent:', err);
    }
};

export const checkRateLimits = async (
    email: string,
    ipAddress?: string
): Promise<{ allowed: boolean; message?: string }> => {
    if (!isConfigured) return { allowed: true };

    try {
        const { data, error } = await supabase.rpc('check_rate_limits', {
            p_email: email,
            p_ip_address: ipAddress || null
        });

        if (error) {
            console.error('Rate limit check error:', error);
            return { allowed: true };
        }

        return data as { allowed: boolean; message?: string };
    } catch (err) {
        console.error('Failed to check rate limits:', err);
        return { allowed: true };
    }
};

// --- ADMIN FUNCTIONS (shared @rehub/admin) ---

export const checkAdminStatus = async (userId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    return checkAdminStatusShared(supabase, userId);
};

/** Uses get_my_admin_role RPC (SECURITY DEFINER) so admin check works for all admins regardless of RLS. Prefer in public viewer. */
export const checkAdminStatusViaRPC = async (): Promise<boolean> => {
    if (!isConfigured) return false;
    try {
        const { data, error } = await supabase.rpc('get_my_admin_role').single();
        if (error || data == null) return false;
        const role = data as string;
        return role === 'admin' || role === 'superadmin' || role === 'editor';
    } catch {
        return false;
    }
};

export type AdminRole = 'admin' | 'superadmin' | 'editor';
export const getAdminRole = async (userId: string): Promise<AdminRole | null> => {
    if (!isConfigured) return null;
    return getAdminRoleShared(supabase, userId);
};

// --- ADMIN: LIST ALL TOURS (for admin panel) ---

export interface AdminTourRow {
    id: string;
    title: string;
    slug: string;
    status: 'draft' | 'published' | 'archived';
    hosting_status: string;
    view_count: number;
    created_at: string;
    user_id: string;
    owner_email: string | null;
}

export const getAdminTours = async (): Promise<AdminTourRow[]> => {
    if (!isConfigured) return [];
    const { data: tours, error: toursError } = await supabase
        .from('tours')
        .select('id, title, slug, status, hosting_status, view_count, created_at, user_id')
        .order('created_at', { ascending: false });

    if (toursError) {
        console.error('getAdminTours tours error:', toursError);
        return [];
    }
    if (!tours?.length) return [];

    const userIds = [...new Set((tours as { user_id: string }[]).map((t) => t.user_id))];
    const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email')
        .in('id', userIds);

    const emailByUserId = new Map<string, string | null>();
    for (const p of profiles || []) {
        emailByUserId.set((p as { id: string; email?: string }).id, (p as { email?: string }).email ?? null);
    }

    return (tours as Array<Record<string, unknown>>).map((t) => ({
        id: t.id as string,
        title: t.title as string,
        slug: t.slug as string,
        status: t.status as AdminTourRow['status'],
        hosting_status: (t.hosting_status as string) ?? 'inactive',
        view_count: (t.view_count as number) ?? 0,
        created_at: t.created_at as string,
        user_id: t.user_id as string,
        owner_email: emailByUserId.get(t.user_id as string) ?? null,
    }));
};

// --- ADMIN: 360WORLDS COLLECTION (admin-curated, in-house) ---

export interface WorldsCollectionRow {
    tour_id: string;
    access: WorldsAccess;
    category: string | null;
    sort: number;
    promo_starts_at: string | null;
    promo_ends_at: string | null;
    promo_title: string | null;
    promo_message: string | null;
}

/** Single tour's worlds_collection row, or null when not in the library. */
export const getWorldsCollectionRow = async (
    tourId: string,
): Promise<WorldsCollectionRow | null> => {
    if (!isConfigured || !tourId) return null;
    const { data, error } = await supabase
        .from('worlds_collection')
        .select('tour_id, access, category, sort, promo_starts_at, promo_ends_at, promo_title, promo_message')
        .eq('tour_id', tourId)
        .maybeSingle();
    if (error) {
        console.error('getWorldsCollectionRow error:', error);
        return null;
    }
    if (!data) return null;
    const r = data as Record<string, unknown>;
    return {
        tour_id: r.tour_id as string,
        access: parseWorldsAccess(r.access),
        category: (r.category as string) ?? null,
        sort: Number(r.sort ?? 0),
        promo_starts_at: (r.promo_starts_at as string) ?? null,
        promo_ends_at: (r.promo_ends_at as string) ?? null,
        promo_title: (r.promo_title as string) ?? null,
        promo_message: (r.promo_message as string) ?? null,
    };
};

/** All tours currently curated into the 360Worlds collection. */
export const getWorldsCollection = async (): Promise<WorldsCollectionRow[]> => {
    if (!isConfigured) return [];
    const { data, error } = await supabase
        .from('worlds_collection')
        .select('tour_id, access, category, sort, promo_starts_at, promo_ends_at, promo_title, promo_message')
        .order('sort', { ascending: true });
    if (error) {
        console.error('getWorldsCollection error:', error);
        return [];
    }
    return (data as Array<Record<string, unknown>>).map((r) => ({
        tour_id: r.tour_id as string,
        access: parseWorldsAccess(r.access),
        category: (r.category as string) ?? null,
        sort: Number(r.sort ?? 0),
        promo_starts_at: (r.promo_starts_at as string) ?? null,
        promo_ends_at: (r.promo_ends_at as string) ?? null,
        promo_title: (r.promo_title as string) ?? null,
        promo_message: (r.promo_message as string) ?? null,
    }));
};

export interface AdminSetWorldsTourOpts {
    included: boolean;
    access?: WorldsAccess;
    category?: string | null;
    sort?: number;
    promo_starts_at?: string | null;
    promo_ends_at?: string | null;
    promo_title?: string | null;
    promo_message?: string | null;
}

/**
 * Admin-only: add/update a tour in the 360Worlds collection, or remove it when
 * `included` is false. Backed by the `admin_set_worlds_tour` RPC (admin-checked,
 * SECURITY DEFINER) so writes never depend on client trust.
 */
export const adminSetWorldsTour = async (
    tourId: string,
    opts: AdminSetWorldsTourOpts
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'Supabase not configured' };
    const { error } = await supabase.rpc('admin_set_worlds_tour', {
        p_tour_id: tourId,
        p_included: opts.included,
        p_access: opts.access ?? 'subscription',
        p_category: opts.category ?? null,
        p_sort: opts.sort ?? 0,
        p_promo_starts_at: opts.promo_starts_at ?? null,
        p_promo_ends_at: opts.promo_ends_at ?? null,
        p_promo_title: opts.promo_title ?? null,
        p_promo_message: opts.promo_message ?? null,
    });
    if (error) {
        return { success: false, error: error.message };
    }
    return { success: true };
};

// --- 360WORLDS CONSUMER SUBSCRIPTION (viewing rights) ---

export type WorldsBillingPlan = 'monthly' | 'annual';
export type WorldsBillingCurrency = 'GBP' | 'USD' | 'EUR';
/** Product tier for a 360Worlds subscription checkout. */
export type WorldsCheckoutPlanType = 'individual' | 'solo_teacher' | 'family' | 'school';

export interface WorldsEntitlement {
    active: boolean;
    is_admin: boolean;
    school_member?: boolean;
    plan: string | null;
    /** Product tier: individual | solo_teacher | family | school (null if none). */
    plan_type?: string | null;
    status: string | null;
    current_period_end: string | null;
    /** Referral bonus access window (free months earned by referring friends). */
    bonus_until?: string | null;
    /** Derived capability limits (-1 = unlimited). Present from the plan-type migration. */
    max_learners?: number;
    /** School licences: teacher-account allowance for the owning school. */
    max_educators?: number;
    /** School licences: small_school | school | large_school (legacy licences resolve to `school`). */
    school_tier?: string | null;
    /** School licences: founding | standard | founding_first_year (null for older rows). */
    school_rate?: string | null;
    max_profiles?: number;
    can_manage_learners?: boolean;
    can_use_classroom?: boolean;
    can_use_remote?: boolean;
    commercial_use?: boolean;
    family_profiles?: boolean;
    school_admin?: boolean;
    /** Gift membership fields (when plan === 'gift'). */
    is_gift?: boolean;
    gift_from?: string | null;
    gift_product_name?: string | null;
}

export interface GiftCardLookup {
    valid: boolean;
    reason?: string | null;
    product_name?: string;
    product_slug?: string;
    product_kind?: string;
    from_name?: string | null;
    recipient_name?: string | null;
    gift_message?: string | null;
    /** Printable/digital card design chosen at checkout. */
    gift_card_theme?: string | null;
    plan_type?: string | null;
    duration_months?: number;
}

export const lookupGiftCard = async (code: string): Promise<GiftCardLookup | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase.rpc('lookup_gift_card', { p_code: code });
    if (error) {
        console.error('lookupGiftCard error:', error);
        return null;
    }
    return (data as GiftCardLookup) ?? null;
};

export const redeemGiftCard = async (
    code: string,
): Promise<{
    success: boolean;
    error?: string;
    product_name?: string;
    plan_type?: string;
    from_name?: string | null;
    period_end?: string;
}> => {
    if (!isConfigured) return { success: false, error: 'Not configured' };
    const { data, error } = await supabase.rpc('redeem_gift_card', { p_code: code });
    if (error) return { success: false, error: error.message };
    return (data as any) ?? { success: false, error: 'Unexpected response' };
};

export interface AdminGiftCardRow {
    id: string;
    code: string | null;
    product_slug: string;
    product_kind: string;
    product_name?: string;
    buyer_name: string | null;
    buyer_email: string | null;
    recipient_name: string | null;
    recipient_email: string | null;
    from_name: string | null;
    gift_card_theme?: string | null;
    status: string;
    is_active: boolean;
    redeemed_at: string | null;
    redeemed_by: string | null;
    delivery_type: string;
    delivery_date: string | null;
    delivery_sent_at: string | null;
    amount_cents: number | null;
    currency: string | null;
    code_expires_at: string | null;
    created_at: string;
}

export const adminListGiftCards = async (opts?: {
    search?: string;
    status?: string;
    productSlug?: string;
    limit?: number;
    offset?: number;
}): Promise<{ total: number; rows: AdminGiftCardRow[] }> => {
    const { data, error } = await supabase.rpc('admin_list_gift_cards', {
        p_search: opts?.search || null,
        p_status: opts?.status || null,
        p_product_slug: opts?.productSlug || null,
        p_limit: opts?.limit ?? 100,
        p_offset: opts?.offset ?? 0,
    });
    if (error) throw error;
    const payload = data as { total?: number; rows?: AdminGiftCardRow[] } | null;
    return { total: payload?.total ?? 0, rows: payload?.rows ?? [] };
};

export const adminDisableGiftCard = async (id: string) => {
    const { data, error } = await supabase.rpc('admin_disable_gift_card', { p_id: id });
    if (error) throw error;
    return data as { success: boolean; error?: string };
};

export const adminEnableGiftCard = async (id: string) => {
    const { data, error } = await supabase.rpc('admin_enable_gift_card', { p_id: id });
    if (error) throw error;
    return data as { success: boolean; error?: string };
};

export const adminResendGiftEmail = async (
    giftCardId: string,
    target: 'purchaser' | 'recipient' = 'recipient',
): Promise<{ ok?: boolean; error?: string }> => {
    const action = target === 'purchaser' ? 'resend_purchaser' : 'send_recipient';
    const { data, error } = await supabase.functions.invoke('worlds-gift-email', {
        body: { action, gift_card_id: giftCardId, resend: true },
    });
    if (error) return { error: error.message };
    if ((data as any)?.error) return { error: (data as any).error };
    return { ok: true };
};

/**
 * Start a 360Worlds Full Access checkout. Separate payment axis from Active
 * Spaces/credits — this grants *viewing* rights to the curated collection. The
 * `api-payment` edge function reads the Stripe price from server secrets, so the
 * client only sends the billing interval. Returns the Stripe Checkout URL.
 */
export const worldsCheckout = async (
    userId: string,
    plan: WorldsBillingPlan,
    opts?: {
        returnUrl?: string;
        cancelUrl?: string;
        currency?: WorldsBillingCurrency;
        /** Product tier — defaults to `individual` for backward compatibility. */
        planType?: WorldsCheckoutPlanType;
        /** School licence tier (school checkouts only). The server decides founding eligibility. */
        schoolTier?: WorldsSchoolTier;
    },
): Promise<{ url?: string; error?: string }> => {
    if (!isConfigured) return { error: 'Payments are not configured.' };
    if (!userId) return { error: 'You must be signed in to subscribe.' };

    const origin = window.location.origin;
    const path = window.location.pathname || '/';
    const returnTo = opts?.returnUrl ?? origin + path;
    const successUrl = `https://hub.rehub.studio/#/purchase-complete?app=worlds&type=subscription&return=${encodeURIComponent(returnTo)}&session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = opts?.cancelUrl ?? `${origin}${path}`;

    // Optional Meta browser cookies (present if the visitor previously accepted
    // marketing cookies on a host that shares them). Used for CAPI match quality.
    const readCookie = (name: string): string | undefined => {
        try {
            const row = document.cookie.split(';').map((c) => c.trim()).find((c) => c.startsWith(`${name}=`));
            return row ? decodeURIComponent(row.slice(name.length + 1)) : undefined;
        } catch {
            return undefined;
        }
    };
    const meta_fbp = readCookie('_fbp');
    const meta_fbc = readCookie('_fbc');
    const meta_event_id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? `initiate_${crypto.randomUUID()}`
            : `initiate_${Date.now()}`;

    try {
        const invokePromise = supabase.functions.invoke('api-payment', {
            body: {
                mode: 'worlds_subscription',
                worlds_plan: plan,
                worlds_plan_type: opts?.planType ?? 'individual',
                ...(opts?.planType === 'school' ? { worlds_school_tier: opts.schoolTier ?? 'school' } : {}),
                currency: opts?.currency ?? 'GBP',
                client_reference_id: userId,
                userId,
                successUrl,
                cancelUrl,
                meta_event_id,
                meta_event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
                meta_client_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
                ...(meta_fbp ? { meta_fbp } : {}),
                ...(meta_fbc ? { meta_fbc } : {}),
            },
            headers: { 'Content-Type': 'application/json' },
        });
        const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) => {
            setTimeout(
                () =>
                    resolve({
                        data: null,
                        error: { message: 'Request timed out. Your network, browser extension, or firewall may be blocking checkout.' },
                    }),
                15000,
            );
        });
        const { data, error } = (await Promise.race([invokePromise, timeoutPromise])) as Awaited<typeof invokePromise>;
        if (error) return { error: error.message };
        if ((data as any)?.error) return { error: (data as any).error };
        if ((data as any)?.url) return { url: (data as any).url as string };
        return { error: 'No payment URL received.' };
    } catch (err: any) {
        return { error: err?.message || 'Checkout failed.' };
    }
};

export interface WorldsSchoolFoundingStatus {
    eligible: boolean;
    /** Why the offer is unavailable, e.g. places_exhausted, already_claimed, legacy_founding. */
    reason: string | null;
    places_remaining: number;
}

/**
 * Whether the signed-in user's school can still claim the Founding School
 * first-year offer. Display-only: checkout re-checks and reserves server-side.
 */
export const getMyWorldsSchoolFoundingStatus = async (): Promise<WorldsSchoolFoundingStatus | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase.rpc('get_my_worlds_school_founding_status');
    if (error) {
        console.warn('getMyWorldsSchoolFoundingStatus error:', error.message);
        return null;
    }
    return (data as WorldsSchoolFoundingStatus) ?? null;
};

/** Founding places left across all school tiers (public; shared 100-school limit). */
export const getWorldsFoundingPlacesRemaining = async (): Promise<number | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase.rpc('worlds_founding_places_remaining');
    if (error) return null;
    return typeof data === 'number' ? data : null;
};

/**
 * Open the 360Worlds billing portal (dedicated 360Worlds Stripe account). Reads
 * the user's `worlds_stripe_customer_id` and creates a portal session via the
 * `customer-portal-worlds` edge function. Returns `{ noCustomer: true }` when
 * the user has never checked out (no worlds customer yet).
 */
export const worldsBillingPortal = async (
    opts?: { returnUrl?: string },
): Promise<{ url?: string; error?: string; noCustomer?: boolean }> => {
    if (!isConfigured) return { error: 'Billing is not configured.' };
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { error: 'You must be signed in.' };

    const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('worlds_stripe_customer_id')
        .eq('id', user.id)
        .single();
    if (profileError) return { error: profileError.message };

    const customerId = (profile as { worlds_stripe_customer_id?: string | null } | null)?.worlds_stripe_customer_id;
    if (!customerId) return { noCustomer: true };

    const returnUrl = opts?.returnUrl ?? (typeof window !== 'undefined' ? window.location.href : undefined);
    const { data, error } = await supabase.functions.invoke('customer-portal-worlds', {
        body: { customerId, returnUrl },
        headers: { 'Content-Type': 'application/json' },
    });
    if (error) return { error: error.message };
    if ((data as any)?.error) return { error: (data as any).error };
    if ((data as any)?.url) return { url: (data as any).url as string };
    return { error: 'Could not open billing portal.' };
};

/**
 * Viewer gate: can the *current* user watch this tour? Free 360Worlds tasters
 * and non-collection tours return true; subscription-only worlds require an
 * active worlds subscription (admins always allowed). Backed by the
 * `can_view_worlds_tour` RPC which reads `auth.uid()` server-side.
 */
export const canViewWorldsTour = async (tourId: string): Promise<boolean> => {
    if (!isConfigured) return true;
    const { data, error } = await supabase.rpc('can_view_worlds_tour', { p_tour_id: tourId });
    if (error) {
        // Fail OPEN: the vast majority of tours are not in the worlds collection,
        // so a transient RPC error must never block normal public viewing. The
        // gate only exists to protect the small curated worlds library.
        console.error('canViewWorldsTour error (allowing view):', error);
        return true;
    }
    return data === true;
};

export interface WorldsPupilAccess {
    /** True when the pupil's school owner has an active worlds/education licence. */
    active: boolean;
    class_id?: string;
    class_name?: string;
    school_id?: string;
}

/**
 * Resolve a class-join pupil's worlds viewing access from their session token.
 * Pupils have no auth account, so the auth-based `can_view_worlds_tour` gate
 * always fails for them; this reads pupil → class → school and reports whether
 * the school's licence is active. Backed by the `get_worlds_pupil_access` RPC
 * (SECURITY DEFINER, callable by anon).
 */
export const getWorldsPupilAccess = async (
    sessionToken: string,
): Promise<WorldsPupilAccess | null> => {
    if (!isConfigured || !sessionToken) return null;
    const { data, error } = await supabase.rpc('get_worlds_pupil_access', {
        p_session_token: sessionToken,
    });
    if (error) {
        console.error('getWorldsPupilAccess error:', error);
        return null;
    }
    const row = data as {
        success?: boolean;
        active?: boolean;
        class_id?: string;
        class_name?: string;
        school_id?: string;
    } | null;
    if (!row?.success) return null;
    return {
        active: !!row.active,
        class_id: row.class_id,
        class_name: row.class_name,
        school_id: row.school_id,
    };
};

export interface WorldsLibraryTour {
    tour_id: string;
    slug: string;
    title: string;
    description: string | null;
    cover_image_url: string | null;
    access: WorldsAccess;
    category: string | null;
    sort: number;
    /** Published + hosted (watchable now). false = curated "coming soon". */
    live: boolean;
    promo_starts_at: string | null;
    promo_ends_at: string | null;
    promo_title: string | null;
    promo_message: string | null;
    /** True when access=promotional and now is inside the promo window. */
    is_promo_active: boolean;
    /** Number of scenes (locations/viewpoints) inside this world. */
    scene_count: number | null;
}

/**
 * Public listing of the 360Worlds collection (published + hosted worlds) with
 * enough tour metadata to render library cards. Playback is still gated per
 * user by `can_view_worlds_tour`. Backed by the `get_worlds_library` RPC.
 */
export const getWorldsLibrary = async (): Promise<WorldsLibraryTour[]> => {
    if (!isConfigured) return [];
    const { data, error } = await supabase.rpc('get_worlds_library');
    if (error) {
        console.error('getWorldsLibrary error:', error);
        return [];
    }
    return ((data as Array<Record<string, unknown>>) ?? []).map((r) => ({
        tour_id: r.tour_id as string,
        slug: r.slug as string,
        title: r.title as string,
        description: (r.description as string) ?? null,
        cover_image_url: (r.cover_image_url as string) ?? null,
        access: parseWorldsAccess(r.access),
        category: (r.category as string) ?? null,
        sort: Number(r.sort ?? 0),
        live: r.live === true,
        promo_starts_at: (r.promo_starts_at as string) ?? null,
        promo_ends_at: (r.promo_ends_at as string) ?? null,
        promo_title: (r.promo_title as string) ?? null,
        promo_message: (r.promo_message as string) ?? null,
        is_promo_active: r.is_promo_active === true,
        scene_count:
            r.scene_count == null || r.scene_count === ''
                ? null
                : Number(r.scene_count),
    }));
};

// --- ADMIN: 360WORLDS COMPLIMENTARY ACCESS (friends/family, teachers, testers) ---

/** 360Worlds comp grant type: 'comp' (friends/family/testers) or 'school' (teachers). */
export type WorldsCompPlan = 'comp' | 'school';

export interface WorldsCompRow {
    user_id: string;
    email: string | null;
    plan: string | null;
    status: string | null;
    current_period_end: string | null;
    /** True while the grant is live (active and not past its expiry). */
    active: boolean;
}

/**
 * Admin-only: comp a user's 360Worlds *viewing* access until an optional expiry
 * (null = no expiry). Looks the user up by email. Backed by the
 * `admin_grant_worlds_access` RPC (admin-checked, SECURITY DEFINER) which
 * refuses to overwrite a paid Stripe subscription.
 */
export const adminGrantWorldsAccess = async (
    email: string,
    expiresAt: string | null,
    plan: WorldsCompPlan = 'comp',
): Promise<{ success: boolean; error?: string; emailed?: boolean }> => {
    if (!isConfigured) return { success: false, error: 'Supabase not configured' };
    const trimmedEmail = email.trim();
    const { error } = await supabase.rpc('admin_grant_worlds_access', {
        p_email: trimmedEmail,
        p_expires_at: expiresAt,
        p_plan: plan,
    });
    if (error) return { success: false, error: error.message };

    // Best-effort: notify the recipient by email. A mail failure must never
    // undo an otherwise-successful grant, so we swallow errors here.
    let emailed = false;
    try {
        const { data } = await supabase.functions.invoke('worlds-notify-access', {
            body: { email: trimmedEmail, plan, expires_at: expiresAt },
        });
        emailed = (data as { emailed?: boolean } | null)?.emailed === true;
    } catch (e) {
        console.error('worlds-notify-access invoke error:', e);
    }

    return { success: true, emailed };
};

/** Admin-only: revoke a complimentary 360Worlds grant. Refuses paid Stripe subs. */
export const adminRevokeWorldsAccess = async (
    email: string,
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'Supabase not configured' };
    const { error } = await supabase.rpc('admin_revoke_worlds_access', { p_email: email.trim() });
    if (error) return { success: false, error: error.message };
    return { success: true };
};

/** Admin-only: list current complimentary (non-Stripe) 360Worlds grants. */
export const getAdminWorldsComps = async (): Promise<WorldsCompRow[]> => {
    if (!isConfigured) return [];
    const { data, error } = await supabase.rpc('admin_list_worlds_comp');
    if (error) {
        console.error('getAdminWorldsComps error:', error);
        return [];
    }
    return ((data as Array<Record<string, unknown>>) ?? []).map((r) => ({
        user_id: r.user_id as string,
        email: (r.email as string) ?? null,
        plan: (r.plan as string) ?? null,
        status: (r.status as string) ?? null,
        current_period_end: (r.current_period_end as string) ?? null,
        active: r.active === true,
    }));
};

/** Filter for the 360Worlds admin user list. */
export type WorldsUserFilter = 'all' | 'active' | 'paid' | 'comp' | 'inactive' | 'free';

/** A 360Worlds-related user row for the admin Users tab. */
export interface WorldsUserRow {
    user_id: string;
    email: string | null;
    created_at: string;
    signup_app: string | null;
    provider: string;
    worlds_active: boolean;
    worlds_plan: string | null;
    worlds_plan_type: string | null;
    worlds_status: string | null;
    worlds_current_period_end: string | null;
    has_stripe_sub: boolean;
    /** True while worlds entitlement is live (active and not past expiry). */
    entitlement_active: boolean;
}

/**
 * Admin-only: list 360Worlds users (signed up via worlds and/or any entitlement).
 * Backed by `admin_list_worlds_users` (admin-checked, SECURITY DEFINER).
 */
export const getAdminWorldsUsers = async (
    filter: WorldsUserFilter = 'all',
    search?: string,
    limit = 500,
    offset = 0,
): Promise<{ users: WorldsUserRow[]; error?: string }> => {
    if (!isConfigured) return { users: [], error: 'Supabase not configured' };
    const { data, error } = await supabase.rpc('admin_list_worlds_users', {
        p_filter: filter,
        p_search: search?.trim() || null,
        p_limit: limit,
        p_offset: offset,
    });
    if (error) {
        console.error('getAdminWorldsUsers error:', error);
        return { users: [], error: error.message };
    }
    return {
        users: ((data as Array<Record<string, unknown>>) ?? []).map((r) => ({
            user_id: r.user_id as string,
            email: (r.email as string) ?? null,
            created_at: r.created_at as string,
            signup_app: (r.signup_app as string) ?? null,
            provider: (r.provider as string) || 'email',
            worlds_active: r.worlds_active === true,
            worlds_plan: (r.worlds_plan as string) ?? null,
            worlds_plan_type: (r.worlds_plan_type as string) ?? null,
            worlds_status: (r.worlds_status as string) ?? null,
            worlds_current_period_end: (r.worlds_current_period_end as string) ?? null,
            has_stripe_sub: r.has_stripe_sub === true,
            entitlement_active: r.entitlement_active === true,
        })),
    };
};

/** A 360Worlds school with its owner, licence status and roll-up counts. */
export interface AdminSchoolRow {
    id: string;
    name: string;
    created_at: string;
    owner_user_id: string;
    owner_email: string | null;
    worlds_active: boolean;
    worlds_plan: string | null;
    worlds_plan_type: string | null;
    worlds_school_rate: string | null;
    worlds_current_period_end: string | null;
    /** True while the school's owner licence is active and unexpired. */
    is_active: boolean;
    teacher_count: number;
    class_count: number;
    pupil_count: number;
}

/**
 * Admin-only: list every 360Worlds school with owner + licence + counts.
 * Backed by the `admin_list_education_schools` RPC (admin-checked, SECURITY
 * DEFINER). Licences are granted/revoked via adminGrantWorldsAccess /
 * adminRevokeWorldsAccess against the owner's email with plan = 'school'.
 */
export const getAdminEducationSchools = async (): Promise<AdminSchoolRow[]> => {
    if (!isConfigured) return [];
    const { data, error } = await supabase.rpc('admin_list_education_schools');
    if (error) {
        console.error('getAdminEducationSchools error:', error);
        return [];
    }
    return ((data as Array<Record<string, unknown>>) ?? []).map((r) => ({
        id: r.id as string,
        name: (r.name as string) ?? '',
        created_at: r.created_at as string,
        owner_user_id: r.owner_user_id as string,
        owner_email: (r.owner_email as string) ?? null,
        worlds_active: r.worlds_active === true,
        worlds_plan: (r.worlds_plan as string) ?? null,
        worlds_plan_type: (r.worlds_plan_type as string) ?? null,
        worlds_school_rate: (r.worlds_school_rate as string) ?? null,
        worlds_current_period_end: (r.worlds_current_period_end as string) ?? null,
        is_active: r.is_active === true,
        teacher_count: Number(r.teacher_count ?? 0),
        class_count: Number(r.class_count ?? 0),
        pupil_count: Number(r.pupil_count ?? 0),
    }));
};

/** Current user's 360Worlds subscription snapshot for the account/paywall UI. */
export const getWorldsEntitlement = async (userId: string): Promise<WorldsEntitlement | null> => {
    if (!isConfigured || !userId) return null;
    const { data, error } = await supabase.rpc('get_worlds_entitlement', { p_user_id: userId });
    if (error) {
        console.error('getWorldsEntitlement error:', error);
        return null;
    }
    return (data as WorldsEntitlement) ?? null;
};

/** A single "friend subscribed" reward on the 360Worlds refer-a-friend program. */
export interface WorldsReferralReward {
    id: string;
    bonus_months: number;
    created_at: string;
    referred_email: string;
}

/** 360Worlds peer-referral dashboard snapshot for the "Refer a friend" UI. */
export interface WorldsReferralStats {
    success: boolean;
    referral_code: string | null;
    bonus_until: string | null;
    /** Friends attributed to this member's code. */
    signup_count: number;
    /** Friends who went on to subscribe (each earns the member a free month). */
    reward_count: number;
    total_bonus_months: number;
    rewards: WorldsReferralReward[];
    error?: string;
}

/**
 * 360Worlds "Refer a friend" stats for the signed-in member: their shareable
 * referral code, how many friends they've referred / converted, and the free
 * months they've earned. Friends who subscribe get their first month free and
 * the referrer earns +1 month of access (see the worlds_referral_program
 * migration + stripe-webhook-worlds).
 */
export const getWorldsReferralStats = async (): Promise<WorldsReferralStats | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase.rpc('get_worlds_referral_stats');
    if (error) {
        console.error('getWorldsReferralStats error:', error);
        return null;
    }
    return (data as WorldsReferralStats) ?? null;
};

/**
 * Superadmin-only: transfer a tour to the account matching `newOwnerEmail`.
 * Backed by the `admin_transfer_tour` RPC (SECURITY DEFINER) which also moves
 * the denormalised user_id on scene_staged_variants / staged_returns.
 */
export const adminTransferTour = async (
    tourId: string,
    newOwnerEmail: string
): Promise<{ success: boolean; newOwnerEmail?: string; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'Supabase not configured' };
    const { data, error } = await supabase.rpc('admin_transfer_tour', {
        p_tour_id: tourId,
        p_new_owner_email: newOwnerEmail.trim(),
    });
    if (error) {
        return { success: false, error: error.message };
    }
    const result = data as { new_owner_email?: string } | null;
    return { success: true, newOwnerEmail: result?.new_owner_email };
};

export interface AdminStudioBuildOrderRow {
    id: string;
    tour_id: string;
    user_id: string;
    status: StudioBuildStatus;
    currency: 'USD' | 'GBP';
    pano_count: number;
    base_price_cents: number;
    additional_price_cents: number;
    rapid_price_cents: number;
    total_price_cents: number;
    created_at: string;
    updated_at: string;
    tour_title: string | null;
    owner_email: string | null;
}

export const getAdminStudioBuildOrders = async (status?: StudioBuildStatus | 'all'): Promise<AdminStudioBuildOrderRow[]> => {
    if (!isConfigured) return [];
    let query = supabase
        .from('studio_build_orders')
        .select('id,tour_id,user_id,status,currency,pano_count,base_price_cents,additional_price_cents,rapid_price_cents,total_price_cents,created_at,updated_at')
        .order('created_at', { ascending: false });

    if (status && status !== 'all') {
        query = query.eq('status', status);
    }

    const { data: rows, error } = await query;
    if (error) {
        console.error('getAdminStudioBuildOrders error:', error);
        return [];
    }
    if (!rows?.length) return [];

    const tourIds = [...new Set((rows as Array<{ tour_id: string }>).map((r) => r.tour_id))];
    const userIds = [...new Set((rows as Array<{ user_id: string }>).map((r) => r.user_id))];

    const [{ data: tours }, { data: profiles }] = await Promise.all([
        supabase.from('tours').select('id,title').in('id', tourIds),
        supabase.from('profiles').select('id,email').in('id', userIds),
    ]);

    const tourTitleById = new Map<string, string | null>();
    for (const t of tours || []) {
        tourTitleById.set((t as { id: string; title?: string }).id, (t as { title?: string }).title ?? null);
    }
    const userEmailById = new Map<string, string | null>();
    for (const p of profiles || []) {
        userEmailById.set((p as { id: string; email?: string }).id, (p as { email?: string }).email ?? null);
    }

    return (rows as Array<Record<string, unknown>>).map((r) => ({
        id: r.id as string,
        tour_id: r.tour_id as string,
        user_id: r.user_id as string,
        status: r.status as StudioBuildStatus,
        currency: ((r.currency as string) === 'GBP' ? 'GBP' : 'USD'),
        pano_count: Number(r.pano_count ?? 0),
        base_price_cents: Number(r.base_price_cents ?? 0),
        additional_price_cents: Number(r.additional_price_cents ?? 0),
        rapid_price_cents: Number(r.rapid_price_cents ?? 0),
        total_price_cents: Number(r.total_price_cents ?? 0),
        created_at: r.created_at as string,
        updated_at: r.updated_at as string,
        tour_title: tourTitleById.get(r.tour_id as string) ?? null,
        owner_email: userEmailById.get(r.user_id as string) ?? null,
    }));
};

export const adminUpdateStudioBuildStatus = async (
    orderId: string,
    nextStatus: StudioBuildStatus
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'Supabase not configured' };
    const { error } = await supabase.rpc('admin_update_studio_build_status', {
        p_order_id: orderId,
        p_status: nextStatus,
    });
    if (error) {
        return { success: false, error: error.message };
    }
    return { success: true };
};

// --- ADMIN: FLOOR PLAN ORDERS (for admin panel) ---

export interface AdminFloorPlanOrderRow {
    id: string;
    user_id: string;
    tour_id: string;
    property_address: string | null;
    number_of_floors: number;
    property_type: FloorPlanPropertyType | null;
    notes: string | null;
    status: FloorPlanOrderStatus;
    credits_charged: number;
    floorplan_pdf_url: string | null;
    floorplan_png_url: string | null;
    production_notes: string | null;
    created_at: string;
    updated_at: string;
    completed_at: string | null;
    tour_title: string | null;
    tour_slug: string | null;
    owner_email: string | null;
}

export const getAdminFloorPlanOrders = async (
    status?: FloorPlanOrderStatus | 'all'
): Promise<AdminFloorPlanOrderRow[]> => {
    if (!isConfigured) return [];
    let query = supabase
        .from('floor_plan_orders')
        .select('*')
        .order('created_at', { ascending: false });

    if (status && status !== 'all') {
        query = query.eq('status', status);
    }

    const { data: rows, error } = await query;
    if (error) {
        console.error('getAdminFloorPlanOrders error:', error);
        return [];
    }
    if (!rows?.length) return [];

    const tourIds = [...new Set((rows as Array<{ tour_id: string }>).map((r) => r.tour_id))];
    const userIds = [...new Set((rows as Array<{ user_id: string }>).map((r) => r.user_id))];

    const [{ data: tours }, { data: profiles }] = await Promise.all([
        supabase.from('tours').select('id,title,slug').in('id', tourIds),
        supabase.from('profiles').select('id,email').in('id', userIds),
    ]);

    const tourById = new Map<string, { title: string | null; slug: string | null }>();
    for (const t of tours || []) {
        const row = t as { id: string; title?: string; slug?: string };
        tourById.set(row.id, { title: row.title ?? null, slug: row.slug ?? null });
    }
    const userEmailById = new Map<string, string | null>();
    for (const p of profiles || []) {
        userEmailById.set((p as { id: string; email?: string }).id, (p as { email?: string }).email ?? null);
    }

    return (rows as Array<Record<string, unknown>>).map((r) => {
        const tour = tourById.get(r.tour_id as string);
        return {
            id: r.id as string,
            user_id: r.user_id as string,
            tour_id: r.tour_id as string,
            property_address: (r.property_address as string) ?? null,
            number_of_floors: Number(r.number_of_floors ?? 1),
            property_type: (r.property_type as FloorPlanPropertyType) ?? null,
            notes: (r.notes as string) ?? null,
            status: r.status as FloorPlanOrderStatus,
            credits_charged: Number(r.credits_charged ?? 0),
            floorplan_pdf_url: (r.floorplan_pdf_url as string) ?? null,
            floorplan_png_url: (r.floorplan_png_url as string) ?? null,
            production_notes: (r.production_notes as string) ?? null,
            created_at: r.created_at as string,
            updated_at: r.updated_at as string,
            completed_at: (r.completed_at as string) ?? null,
            tour_title: tour?.title ?? null,
            tour_slug: tour?.slug ?? null,
            owner_email: userEmailById.get(r.user_id as string) ?? null,
        };
    });
};

export const adminUpdateFloorPlanOrder = async (
    orderId: string,
    fields: {
        status?: FloorPlanOrderStatus;
        productionNotes?: string | null;
        pdfUrl?: string | null;
        pngUrl?: string | null;
    }
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'Supabase not configured' };
    const { error } = await supabase.rpc('admin_update_floor_plan_order', {
        p_order_id: orderId,
        p_status: fields.status ?? null,
        p_production_notes: fields.productionNotes ?? null,
        p_pdf_url: fields.pdfUrl ?? null,
        p_png_url: fields.pngUrl ?? null,
    });
    if (error) {
        return { success: false, error: error.message };
    }
    return { success: true };
};
